import { ACCOUNT_TYPE_ROLES, type AccountType, type ProfessionalRegistration } from './accounts';
import { DEFAULT_RECORD_HOLDER, PRACTICE_ROLES, type PracticeType, type RecordHolder, type Role } from './roles';

/**
 * pending: waiting for the platform's verification staff. Clinics, small
 *   hospitals and pharmacies start here. Members can be invited, but no
 *   records flow until the practice is active.
 * active: verified, or a solo practice of a verified doctor.
 * rejected: verification failed. The practice admin sees the reason.
 * suspended: blocked by the platform admin.
 */
export const PRACTICE_STATUSES = ['pending', 'active', 'rejected', 'suspended'] as const;
export type PracticeStatus = (typeof PRACTICE_STATUSES)[number];

export const MEMBERSHIP_STATUSES = ['active', 'removed'] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

/**
 * Practice roles each kind of account may hold. The professional roles need
 * the matching verified account, and front desk is for clinic staff accounts.
 */
export const MEMBER_ROLES_BY_ACCOUNT: Readonly<Record<AccountType, readonly Role[]>> = {
  patient: [],
  doctor: ['doctor', 'practice_admin'],
  pharmacist: ['pharmacist', 'practice_admin'],
  staff: ['practice_admin', 'front_desk'],
  platform_admin: [],
  hub: [],
};

/** Accounts that may set up each kind of practice. */
export const PRACTICE_FOUNDERS: Readonly<Record<PracticeType, readonly AccountType[]>> = {
  solo: ['doctor'],
  clinic: ['doctor', 'staff'],
  small_hospital: ['doctor', 'staff'],
  pharmacy: ['pharmacist', 'staff'],
};

/** Where each kind of practice may keep its records. A pharmacy with several counters may use a hub. */
export const ALLOWED_RECORD_HOLDERS: Readonly<Record<PracticeType, readonly RecordHolder[]>> = {
  solo: ['device'],
  clinic: ['hub'],
  small_hospital: ['hub'],
  pharmacy: ['device', 'hub'],
};

/**
 * When records live on a member's own device, the role that member must hold:
 * the doctor of a solo practice, or a pharmacist of a pharmacy.
 */
export const HOLDER_ROLE: Readonly<Record<PracticeType, Role>> = {
  solo: 'doctor',
  clinic: 'doctor',
  small_hospital: 'doctor',
  pharmacy: 'pharmacist',
};

/** Solo practices start active, because their only member is a doctor the platform has already verified. */
export function initialPracticeStatus(type: PracticeType): PracticeStatus {
  return type === 'solo' ? 'active' : 'pending';
}

export function defaultRecordHolder(type: PracticeType): RecordHolder {
  return DEFAULT_RECORD_HOLDER[type];
}

/** The founder's roles: practice admin, plus their own professional role when the practice has it. */
export function founderRoles(practiceType: PracticeType, accountType: AccountType): Role[] {
  const professional = ACCOUNT_TYPE_ROLES[accountType].filter((role) => PRACTICE_ROLES[practiceType].includes(role));
  return ['practice_admin', ...professional];
}

/** Why this account cannot hold these roles in this kind of practice, or null when it can. */
export function memberRolesProblem(practiceType: PracticeType, accountType: AccountType, roles: readonly Role[]): string | null {
  if (roles.length === 0) return 'Choose at least one role';
  if (new Set(roles).size !== roles.length) return 'List each role once';
  for (const role of roles) {
    if (!PRACTICE_ROLES[practiceType].includes(role)) return `This kind of practice has no ${role} role`;
    if (!MEMBER_ROLES_BY_ACCOUNT[accountType].includes(role)) return `A ${accountType} account cannot hold the ${role} role`;
  }
  return null;
}

/** Days an invitation code stays usable, and days the admin then has to approve the person who used it. */
export const INVITATION_TTL_DAYS = 7;
/** Most practices one account may belong to at once, for example a visiting doctor. */
export const MAX_PRACTICES_PER_ACCOUNT = 20;
/** Most practices one account may have waiting for verification. */
export const MAX_PENDING_PRACTICES_PER_ACCOUNT = 3;
export const MAX_MEMBERS_PER_PRACTICE = 500;
export const MAX_OPEN_INVITATIONS_PER_PRACTICE = 50;
export const MAX_PHARMACY_LINKS_PER_DOCTOR = 5;

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const CODE_LENGTH = 10;

/**
 * A one-time code for joining a practice or pairing a hub: 50 random bits as
 * ten Crockford base32 characters, shown as XXXXX-XXXXX. The server keeps
 * only its SHA-256.
 */
export function generateJoinCode(): string {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(CODE_LENGTH));
  // 256 is a multiple of 32, so the low five bits of each byte are uniform.
  const code = Array.from(bytes, (byte) => CROCKFORD[byte & 31]).join('');
  return `${code.slice(0, 5)}-${code.slice(5)}`;
}

/**
 * Reads a code as typed. Ignores case, spaces and dashes, and accepts O for 0
 * and I or L for 1. Returns the ten plain characters, or null.
 */
export function normalizeJoinCode(text: string): string | null {
  const cleaned = text
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
  if (cleaned.length !== CODE_LENGTH || [...cleaned].some((char) => !CROCKFORD.includes(char))) return null;
  return cleaned;
}

/** A clinic's or pharmacy's own registration, such as a clinical establishment number or a drug licence. */
export interface PracticeRegistration {
  authority: string;
  number: string;
}

/** Longest letterhead address, and the pattern a letterhead phone number must match. */
export const MAX_PRACTICE_ADDRESS_LENGTH = 200;
export const PRACTICE_PHONE_PATTERN = /^\+?[0-9][0-9 -]{5,18}[0-9]$/;

/** A practice as its members see it. */
export interface PublicPractice {
  id: string;
  name: string;
  type: PracticeType;
  city: string;
  status: PracticeStatus;
  statusReason?: string;
  registration?: PracticeRegistration;
  /** The address printed on the practice's letterhead. */
  address?: string;
  /** The phone number printed on the practice's letterhead. */
  phone?: string;
  recordHolder: RecordHolder;
  /** Devices that hold the practice's records. Members send their requests there. */
  recordHolderDeviceIds: string[];
  createdAt: string;
}

/** One of the caller's practices, with the roles the caller holds there. */
export interface MyPractice {
  practice: PublicPractice;
  roles: Role[];
}

/** A member as the practice admin sees them. */
export interface PracticeMember {
  accountId: string;
  name: string;
  accountType: AccountType;
  mobile: string;
  registration?: ProfessionalRegistration;
  roles: Role[];
  since: string;
}

/**
 * open: waiting for someone to enter the code. accepted: someone did, and the
 * practice admin must approve them. approved and cancelled are final. expired:
 * an open or accepted invitation ran out of time.
 */
export const INVITATION_STATUSES = ['open', 'accepted', 'approved', 'cancelled', 'expired'] as const;
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

/** The person who used an invitation code, shown to the practice admin before approval. */
export interface InvitationAcceptor {
  accountId: string;
  name: string;
  accountType: AccountType;
  registration?: ProfessionalRegistration;
}

export interface PracticeInvitation {
  id: string;
  practiceId: string;
  roles: Role[];
  /** The admin's own note, such as who the code is for. */
  label?: string;
  status: InvitationStatus;
  createdAt: string;
  expiresAt: string;
  acceptedBy?: InvitationAcceptor;
  acceptedAt?: string;
}

/** An invitation the caller used, as they see it while waiting for approval. */
export interface MyInvitation {
  id: string;
  practice: { id: string; name: string; type: PracticeType; city: string };
  roles: Role[];
  status: InvitationStatus;
  acceptedAt: string;
  expiresAt: string;
}

/** requested: the doctor asked. active: the pharmacy accepted. */
export const PHARMACY_LINK_STATUSES = ['requested', 'active'] as const;
export type PharmacyLinkStatus = (typeof PHARMACY_LINK_STATUSES)[number];

/** A doctor's link to a pharmacy that receives their prescriptions directly. */
export interface PharmacyLink {
  id: string;
  status: PharmacyLinkStatus;
  doctor: { accountId: string; name: string; registration?: ProfessionalRegistration };
  pharmacy: { id: string; name: string; city: string };
  requestedAt: string;
  acceptedAt?: string;
}

/** A pharmacy in the directory that doctors and patients search. */
export interface DirectoryPharmacy {
  id: string;
  name: string;
  city: string;
}
